import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import {
  CreateInvestorRequest,
  DocumentCreateRequest,
  DocumentUpdateRequest,
  DeleteBlocker,
  DeleteConflictResponse,
  DeleteInvestorResult,
  Investor,
  InvestorApiItem,
  InvestorDocumentItem,
  InvestorGroupOption,
  InvestorNoteItem,
  InvestorTypeOption,
  NoteUpsertRequest,
  UpdateInvestorRequest,
} from './investor.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class InvestorService {
  private readonly endpoint = apiUrl('investor');
  private readonly investorTypeEndpoint = apiUrl('investortype');
  private readonly investorGroupEndpoint = apiUrl('investorgroup');
  private readonly documentsEndpoint = apiUrl('document');
  private readonly notesEndpoint = apiUrl('Notes');

  constructor(private readonly http: HttpClient) {}

  getInvestors(): Observable<Investor[]> {
    return this.http.get<unknown>(this.endpoint).pipe(
      map((response) => this.normalizeInvestors(response)),
    );
  }

  getInvestorTypes(): Observable<InvestorTypeOption[]> {
    return this.http.get<unknown>(this.investorTypeEndpoint).pipe(
      map((response) => this.mapRows(response, (item) => this.toLookupOption(item))),
    );
  }

  getInvestorGroups(): Observable<InvestorGroupOption[]> {
    return this.http.get<unknown>(this.investorGroupEndpoint).pipe(
      map((response) => this.mapRows(response, (item) => this.toLookupOption(item))),
    );
  }

  createInvestor(payload: CreateInvestorRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, payload);
  }

  updateInvestor(investorId: number, payload: UpdateInvestorRequest): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/${investorId}`, payload);
  }

  deleteInvestor(investorId: number): Observable<DeleteInvestorResult> {
    return this.http.delete<void>(`${this.endpoint}/${investorId}`, { observe: 'response' }).pipe(
      map((): DeleteInvestorResult => ({ kind: 'success' })),
      catchError((error: unknown) => of(this.mapDeleteError(error))),
    );
  }

  getDocumentsByParent(parentElementId: string): Observable<InvestorDocumentItem[]> {
    return this.http.get<unknown>(`${this.documentsEndpoint}/trade/${encodeURIComponent(parentElementId)}`).pipe(
      map((response) => this.normalizeDocuments(response)),
    );
  }

  createDocument(payload: DocumentCreateRequest): Observable<InvestorDocumentItem> {
    const formData = new FormData();
    formData.append('Filename', payload.filename);
    if (payload.parentElementId) {
      formData.append('ParentElementId', payload.parentElementId);
    }
    if (payload.securityId) {
      formData.append('SecurityId', payload.securityId);
    }
    formData.append('Description', payload.description ?? '');
    formData.append('Type', payload.type);
    formData.append('Source', payload.source);
    formData.append('ModifiedBy', payload.modifiedBy);
    formData.append('File', payload.file);

    return this.http.post<unknown>(this.documentsEndpoint, formData).pipe(
      map((response) => this.toDocument(this.extractResponseRecord(response))),
    );
  }

  updateDocument(id: number, payload: DocumentUpdateRequest): Observable<InvestorDocumentItem> {
    return this.http.put<unknown>(`${this.documentsEndpoint}/${encodeURIComponent(String(id))}`, payload).pipe(
      map((response) => this.toDocument(this.extractResponseRecord(response))),
    );
  }

  deleteDocument(id: number): Observable<void> {
    return this.http.delete<void>(`${this.documentsEndpoint}/${encodeURIComponent(String(id))}`);
  }

  getNotes(): Observable<InvestorNoteItem[]> {
    return this.http.get<unknown>(this.notesEndpoint).pipe(
      map((response) => this.normalizeNotes(response)),
    );
  }

  createNote(payload: NoteUpsertRequest): Observable<InvestorNoteItem> {
    return this.http.post<unknown>(this.notesEndpoint, payload).pipe(
      map((response) => this.normalizeNote(response)),
    );
  }

  updateNote(id: number, payload: NoteUpsertRequest): Observable<void> {
    return this.http.put<void>(`${this.notesEndpoint}/${encodeURIComponent(String(id))}`, payload);
  }

  deleteNote(id: number): Observable<void> {
    return this.http.delete<void>(`${this.notesEndpoint}/${encodeURIComponent(String(id))}`);
  }

  private normalizeInvestors(response: unknown): Investor[] {
    return this.mapRows(response, (item) => ({
      id: this.toNumber(item.id),
      name: this.toText(item.name),
      investorTypeId: this.toNumber(item.investorTypeId),
      countryId: this.toNumber(item.countryId),
      regionId: this.toNumber(item.regionId),
      investorGroupId: this.toNumber(item.investorGroupId),
      investorTypeName: this.toText(item.investorTypeName),
      country: this.toText(item.country),
      region: this.toText(item.region),
      investorGroupName: this.toText(item.investorGroupName),
    }));
  }

  private mapRows<T>(response: unknown, mapper: (item: InvestorApiItem) => T): T[] {
    return this.extractRows(response).map(mapper);
  }

  private toLookupOption(item: InvestorApiItem): { id: number; name: string } {
    return {
      id: this.toNumber(item.id),
      name: this.toText(item.name),
    };
  }

  private extractRows(response: unknown): InvestorApiItem[] {
    // API payloads can come as a bare array or wrapped under data/items.
    if (Array.isArray(response)) {
      return response as InvestorApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as InvestorApiItem[];
      }

      if (Array.isArray(items)) {
        return items as InvestorApiItem[];
      }
    }

    return [];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }

  private mapDeleteError(error: unknown): DeleteInvestorResult {
    if (!(error instanceof HttpErrorResponse)) {
      return {
        kind: 'error',
        message: 'Unable to delete investor at this time. Please try again.',
      };
    }

    if (error.status === HttpStatusCode.Conflict) {
      const conflict = this.normalizeDeleteConflict(error.error);

      return {
        kind: 'conflict',
        payload: conflict,
      };
    }

    return {
      kind: 'error',
      message: 'Unable to delete investor at this time. Please try again.',
    };
  }

  private normalizeDeleteConflict(response: unknown): DeleteConflictResponse {
    const record = this.toRecord(response);
    const blockersRaw = Array.isArray(record['blockers'])
      ? (record['blockers'] as unknown[])
      : [];

    return {
      title: this.toText(record['title']) || 'Investor cannot be deleted.',
      status: this.toNumber(record['status']) || HttpStatusCode.Conflict,
      detail: this.toText(record['detail']) || 'Investor is linked to trades and cannot be deleted.',
      blockers: blockersRaw.map((blocker) => this.normalizeDeleteBlocker(blocker)),
    };
  }

  private normalizeDocuments(response: unknown): InvestorDocumentItem[] {
    if (Array.isArray(response)) {
      return response.map((entry) => this.toDocument(this.toRecord(entry)));
    }

    const record = this.toRecord(response);
    const data = record['data'];
    if (Array.isArray(data)) {
      return data.map((entry) => this.toDocument(this.toRecord(entry)));
    }

    return [];
  }

  private toDocument(record: Record<string, unknown>): InvestorDocumentItem {
    return {
      id: this.toNumber(record['id'] ?? record['Id'] ?? record['documentId'] ?? record['DocumentId']),
      parentElementId: this.toText(record['parentElementId'] ?? record['ParentElementId']),
      securityId: this.toText(record['securityId'] ?? record['SecurityId']),
      description: this.toText(record['description'] ?? record['Description'] ?? record['name'] ?? record['Name']),
      type: this.toText(record['type'] ?? record['Type'] ?? record['documentType'] ?? record['DocumentType']),
      filename: this.toText(record['filename'] ?? record['Filename'] ?? record['fileName'] ?? record['FileName']),
      ezShareId: this.toText(record['ezShareId'] ?? record['EzShareId'] ?? record['eZShareId'] ?? record['EZShareId']),
      ezShareLink: this.toText(record['ezShareLink'] ?? record['EzShareLink'] ?? record['eZShareLink'] ?? record['EZShareLink']),
      source: this.toText(record['source'] ?? record['Source']),
    };
  }

  private normalizeNotes(response: unknown): InvestorNoteItem[] {
    if (Array.isArray(response)) {
      return response.map((entry) => this.toNote(this.toRecord(entry)));
    }

    const record = this.toRecord(response);
    const data = record['data'];
    if (Array.isArray(data)) {
      return data.map((entry) => this.toNote(this.toRecord(entry)));
    }

    return [];
  }

  private normalizeNote(response: unknown): InvestorNoteItem {
    const record = this.toRecord(response);
    const data = this.toRecord(record['data']);
    return Object.keys(data).length > 0
      ? this.toNote(data)
      : this.toNote(record);
  }

  private toNote(record: Record<string, unknown>): InvestorNoteItem {
    return {
      id: this.toNumber(record['id'] ?? record['Id']),
      parentId: this.toText(record['parentId'] ?? record['ParentId']),
      source: this.toText(record['source'] ?? record['Source']),
      description: this.toText(record['description'] ?? record['Description']),
      modifiedBy: this.toText(record['modifiedBy'] ?? record['ModifiedBy']),
      dateModified: this.toText(record['dateModified'] ?? record['DateModified']),
    };
  }

  private extractResponseRecord(response: unknown): Record<string, unknown> {
    const record = this.toRecord(response);
    const data = this.toRecord(record['data']);

    return Object.keys(data).length > 0 ? data : record;
  }

  private normalizeDeleteBlocker(blocker: unknown): DeleteBlocker {
    const record = this.toRecord(blocker);

    return {
      tradeId: this.toText(record['tradeId']),
      dealId: this.toText(record['dealId']),
    };
  }

  private toRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : {};
  }
}
